import { MouseEvent, ReactElement, useCallback } from 'react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { Tooltip } from '@nx-frontend/shared/ui/tooltip';
import { UiTooltipPosition } from '@nx-frontend/shared/types';
import { isValidUrl } from '@nx-frontend/shared/utils';

export interface ToggleTitleProps {
  title: string;
  isExpanded: boolean;
  isActive?: boolean;
  id?: string;
  icon?: ReactElement;
  iconUrl?: string | null;
  moreTooltip: string;
  addTooltip: string;
  moreDisabled?: boolean;
  addDisabled?: boolean;
  toggle: () => void;
  moreClick: (event: MouseEvent) => void;
  addClick: (event: MouseEvent) => void;
  rightClick?: (event: MouseEvent) => void;
  itemClick?: (event: MouseEvent) => void;
}
export const ToggleTitle: React.FC<ToggleTitleProps> = ({
  title,
  isExpanded,
  isActive,
  toggle,
  moreDisabled,
  addClick,
  moreClick,
  addDisabled,
  icon,
  iconUrl,
  moreTooltip,
  addTooltip,
  id,
  rightClick,
  itemClick,
}) => {
  const handleContextMenu = useCallback(
    (e: MouseEvent) => {
      if (rightClick) {
        e.preventDefault();
        rightClick(e);
      }
    },
    [rightClick]
  );

  return (
    <div
      data-cy={title.toLowerCase().replace(' ', '-')}
      className={classNames('flex items-center py-2 rounded cursor-pointer pr-4 group', {
        'bg-teal-100 font-semibold': isActive,
        'hover:bg-neutral-100': !isActive,
      })}
      onContextMenu={handleContextMenu}
    >
      <div onClick={toggle}>
        <Icon
          icon={IconsMap.ARROW_DOWN}
          className={classNames('w-4 h-4 text-neutral-500 -rotate-90 transition', {
            'rotate-0': isExpanded,
          })}
        />
      </div>
      <div className="flex items-center min-w-0" onClick={itemClick}>
        <div>
          <div className="w-4 h-4 text-neutral-500 mr-2">
            {iconUrl && isValidUrl(iconUrl) ? (
              <img src={iconUrl} alt="" className="max-w-4 max-h-4" />
            ) : icon ? (
              <Icon icon={icon} />
            ) : (
              <span className="rounded max-w-4 max-h-4 flex align-center justify-center text-[10px] bg-cyan-900 text-white">
                {getIconLetters(title)}
              </span>
            )}
          </div>
        </div>
        <span className="text-sm whitespace-nowrap text-ellipsis overflow-hidden mr-2" title={title}>
          {title}
        </span>
      </div>
      <div className="ml-auto">
        <div className="flex items-center" data-cy="more">
          {!moreDisabled && (
            <Tooltip message={moreTooltip} position={UiTooltipPosition.TOP_LEFT}>
              <div onClick={moreClick}>
                <Icon
                  icon={IconsMap.MORE_HORIZONTAL}
                  className="w-4 h-4 rounded hover:bg-neutral-200 text-neutral-500 mr-2"
                />
              </div>
            </Tooltip>
          )}

          {!addDisabled && (
            <Tooltip message={addTooltip} position={UiTooltipPosition.TOP_LEFT}>
              <div>
                <div
                  onClick={addClick}
                  className="w-6 h-6 border rounded border-cyan-900 flex items-center justify-center"
                >
                  <Icon icon={IconsMap.PLUS} className="w-5 h-5 text-cyan-900" />
                </div>
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

function getIconLetters(icon: string) {
  const words = icon.split(' ');
  if (words.length > 1) {
    return words[0].slice(0, 1) + words[1].slice(0, 1);
  } else {
    return icon.slice(0, 2);
  }
}
